import {Component, HostListener, Input, NgZone, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {Layer} from 'storyboard-renderer';
import {TimelineService} from 'app/storyboard/storyboard-edit/timeline/timeline.service';
import {Storyboard} from 'storyboard-renderer';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Clip} from 'storyboard-renderer';
import {DragService} from '../../shared/drag.service';
import {Template} from "../../../template/shared/template";
import {TimeTrackingService} from "../../shared/time-tracking.service";

@Component({
    selector: 'app-timeline',
    templateUrl: './timeline.component.html',
    styleUrls: ['./timeline.component.scss']
})
export class TimelineComponent implements OnInit, OnDestroy {

    @Input() storyboard: Storyboard | Template;
    creatingLayer = false;
    transcriptionEditing = false;

    interval;

    constructor(private dragService: DragService, private timelineService: TimelineService,
                private modalService: NgbModal, private zone: NgZone, private renderer: Renderer2, private timeTrackingService: TimeTrackingService) {
    }

    ngOnInit() {

        if (!this.timelineService.getStoryboard){
          this.timelineService.setStoryboard(this.storyboard);
        }

        this.dragService.setMouseMove((event) => this.mouseMove(event));
        this.dragService.setMouseUp((event, relativeMovement) => this.mouseUp(event, relativeMovement));

        // Trigger view refresh and update time tracking. The
        setInterval(() => {
            this.timeTrackingService.setCurrentTime(this.timelineService.getPlayerControl().getCurrentTime());
        }, 40);

    }

    ngOnDestroy(): void {
      clearInterval(this.interval);
    }

    public onDropSuccess(): void {
        this.timelineService.addCurrentVersionToHistory();
    }

    mouseUp(event, relativeMovement) {
        event.preventDefault();

        switch (this.dragService.getDragType()) {

            case DragService.DRAG_TYPE.CLIP_EXTEND_RIGHT:

                this.dragService.getDragElement().clip.setDuration(this.calculateClipDurationOnRightExtend(relativeMovement));

                // Trigger Storyboard Object Change
                this.timelineService.storyboardChangeEvent.next(this.storyboard);

                break;

            case DragService.DRAG_TYPE.CLIP_EXTEND_LEFT:

                const duration = this.calculateClipDurationOnLeftExtend(relativeMovement);
                this.dragService.getDragElement().clip.setDuration(duration);

                this.dragService.getDragElement().clip.setStartsAt(this.calculateClipPositionOnLeftExtend(relativeMovement));

                // Trigger Storyboard Object Change
                this.timelineService.storyboardChangeEvent.next(this.storyboard);

                break;

            case DragService.DRAG_TYPE.CLIP_MOVE:
                const clip = this.dragService.getDragElement().clip;
                const startsAt = this.timelineService.convertPxToTime(this.calculateClipPosition(event));

                if (startsAt !== clip.starts_at.milliseconds) {
                    this.dragService.getDragElement().clip.setStartsAt(startsAt);

                    // Trigger Storyboard Object Change
                    this.timelineService.storyboardChangeEvent.next(this.storyboard);
                }

                break;

            case DragService.DRAG_TYPE.TIMELINE_MOVE:

                this.timelineService.setTimelinePosition(this.calculateTimelinePosition(relativeMovement));

                break;
        }

        this.timelineService.isClipExtending = false;

    }

    mouseMove(event) {
        event.preventDefault();

        const relativeMovement = this.dragService.calculateRelativeXMovement(event);

        switch (this.dragService.getDragType()) {

            case DragService.DRAG_TYPE.CLIP_EXTEND_RIGHT:

                const w = this.timelineService.convertTimeToPx(this.calculateClipDurationOnRightExtend(relativeMovement));
                this.dragService.getDragElement().element.nativeElement.style.width = w + 'px';

                if (w < 150) {
                    this.renderer.addClass(this.dragService.getDragElement().element.nativeElement, 'short-clip');
                } else {
                    this.renderer.removeClass(this.dragService.getDragElement().element.nativeElement, 'short-clip');
                }

                break;

            case DragService.DRAG_TYPE.CLIP_EXTEND_LEFT:

                const width = this.timelineService.convertTimeToPx(this.calculateClipDurationOnLeftExtend(relativeMovement));

                this.dragService.getDragElement().element.nativeElement.style.width = width + 'px';

                if (width < 150) {
                    this.renderer.addClass(this.dragService.getDragElement().element.nativeElement, 'short-clip');
                } else {
                    this.renderer.removeClass(this.dragService.getDragElement().element.nativeElement, 'short-clip');
                }

                this.dragService.getDragElement().element.nativeElement.style.left =
                    this.timelineService.convertTimeToPx(this.calculateClipPositionOnLeftExtend(relativeMovement)) + 'px';

                break;

            case DragService.DRAG_TYPE.CLIP_MOVE:

                // If the clip is just dragging to right or left, so we just change the position of the clip
                this.dragService.getDragElement().element.nativeElement.style.left = this.calculateClipPosition(event) + 'px';

                break;

            case DragService.DRAG_TYPE.TIMELINE_MOVE:

                this.timelineService.setTimelinePosition(this.calculateTimelinePosition(relativeMovement));

                break;
        }
    }

    snappingLeftAndRight(left) {
        const clipLayer = this.findClipLayer(this.dragService.getDragElement().clip);

        if (!clipLayer) {
            return left;
        }

        for (let i = 0; i < clipLayer.getClips().length; i++) {
            let clip = clipLayer.getClips()[i];
            if (this.dragService.getDragElement().clip === clip) {
                continue;
            }

            // If the left side of the dragged element is 10px close to the closest element's end
            if (Math.abs(this.timelineService.convertPxToTime(left) -
                clip.starts_at.add(clipLayer.getClips()[i].duration).as_milliseconds) <=
                    this.timelineService.convertPxToTime(10)) {

                return this.timelineService.convertTimeToPx(clip.starts_at.add(clip.duration).as_milliseconds);
            }

            // If the right side of the dragged element is 10px close to the closest element's start
            if (Math.abs(this.timelineService.convertPxToTime(left) + this.dragService.getDragElement().clip.duration.as_milliseconds -
                    clip.starts_at.as_milliseconds) <=
                this.timelineService.convertPxToTime(10)) {

                return this.timelineService.convertTimeToPx(
                    clip.starts_at.sub(this.dragService.getDragElement().clip.duration).as_milliseconds
                );
            }
        }

        return left;
    }

    snappingLeftDuration(duration) {

        const clipLayer = this.findClipLayer(this.dragService.getDragElement().clip);

        if (!clipLayer) {
            return duration;
        }

        for (let i = 0; i < clipLayer.getClips().length; i++) {
            let clip = clipLayer.getClips()[i];
            if (this.dragService.getDragElement().clip === clip) {
                continue;
            }

            // If the right side of the dragged element is 10px close to the closest element's start
            if (Math.abs(this.dragService.getDragStartPositions().clip.position + this.dragService.getDragStartPositions().clip.length -
                    duration - clip.starts_at.add(clip.duration).as_milliseconds) <=
                this.timelineService.convertPxToTime(10)) {

                return this.dragService.getDragStartPositions().clip.position +
                    this.dragService.getDragStartPositions().clip.length -
                    clip.starts_at.add(clip.duration).as_milliseconds;
            }
        }

        return duration;
    }

    snappingLeft(position) {
        const clipLayer = this.findClipLayer(this.dragService.getDragElement().clip);

        if (!clipLayer) {
            return position;
        }

        for (let i = 0; i < clipLayer.getClips().length; i++) {
            let clip = clipLayer.getClips()[i];
            if (this.dragService.getDragElement().clip === clip) {
                continue;
            }

            // If the right side of the dragged element is 10px close to the closest element's start
            if (Math.abs(position -
                    clip.starts_at.add(clip.duration).as_milliseconds) <=
                this.timelineService.convertPxToTime(10)) {

                return clip.starts_at.add(clip.duration).as_milliseconds;
            }
        }

        return position;
    }

    snappingRight(duration) {
        const clipLayer = this.findClipLayer(this.dragService.getDragElement().clip);

        if (!clipLayer) {
            return duration;
        }

        for (let i = 0; i < clipLayer.getClips().length; i++) {
            let clip = clipLayer.getClips()[i];
            if (this.dragService.getDragElement().clip === clip) {
                continue;
            }

            // If the right side of the dragged element is 10px close to the closest element's start
            if (Math.abs(this.dragService.getDragElement().clip.starts_at.as_milliseconds + duration -
                    clip.starts_at.as_milliseconds) <=
                this.timelineService.convertPxToTime(10)) {

                return clip.starts_at.sub(this.dragService.getDragElement().clip.starts_at).as_milliseconds;
            }
        }

        return duration;
    }



    findClipLayer(clip: Clip) {
        for (let i = 0; i < this.storyboard.timeline.layers.length; i++) {
            for (let j = 0; j < this.storyboard.timeline.layers[i].getClips().length; j++) {
                if (clip === this.storyboard.timeline.layers[i].getClips()[j]) {
                    return this.storyboard.timeline.layers[i];
                }
            }
        }

        return null;
    }

    calculateClipDurationOnLeftExtend(relativeMovement) {
        return this.snappingLeftDuration(Math.max(this.timelineService.convertPxToTime(100),
            Math.min(this.dragService.getDragStartPositions().clip.length +
                this.dragService.getDragStartPositions().clip.position,
                this.dragService.getDragStartPositions().clip.length -
                this.timelineService.convertPxToTime(relativeMovement))
        ))
    }

    calculateClipDurationOnRightExtend(relativeMovement) {
        return this.snappingRight(
            Math.min(this.storyboard.getDuration().as_milliseconds - this.dragService.getDragStartPositions().clip.position,
                Math.max(this.timelineService.convertPxToTime(100), this.dragService.getDragStartPositions().clip.length +
                    this.timelineService.convertPxToTime(relativeMovement))
            )
        );
    }

    calculateClipPositionOnLeftExtend(relativeMovement) {
        return this.snappingLeft(Math.min(this.dragService.getDragStartPositions().clip.position +
            this.dragService.getDragStartPositions().clip.length - this.timelineService.convertPxToTime(100),
            Math.max(0, this.dragService.getDragStartPositions().clip.position +
                this.timelineService.convertPxToTime(relativeMovement)
            )
        ))
    }

    calculateClipPosition(event) {
        const boundingBox = {
            left: 0,
            right: this.timelineService.convertTimeToPx(this.storyboard.getDuration().as_milliseconds -
                this.dragService.getDragElement().clip.duration.as_milliseconds)
        };

        return this.snappingLeftAndRight(this.dragService.getPositionX(event, boundingBox));
    }

    calculateTimelinePosition(relativeMovement) {
        const storyboardLengthDiff = this.timelineService.convertTimeToPx(this.storyboard.getDuration().as_milliseconds)
            - this.timelineService.getTimelineWidthInPx();

        return Math.max(-storyboardLengthDiff,
            Math.min(0, this.dragService.getDragStartPositions().element.x + relativeMovement));
    }

    swapLayerCreatingMode() {
        this.creatingLayer = !this.creatingLayer;
    }

    newLayer(layer: Layer) {
        this.storyboard.timeline.layers.unshift(layer);

        // Trigger Storyboard Object Change
        this.timelineService.storyboardChangeEvent.next(this.storyboard);

        this.swapLayerCreatingMode();
    }

    deleteLayer(deletedLayer: Layer) {
        this.storyboard.timeline.layers = this.storyboard.timeline.layers.filter((layer: Layer) => layer !== deletedLayer);

        // Trigger Storyboard Object Change
        this.timelineService.storyboardChangeEvent.next(this.storyboard);
    }

    mouseEnter(event, layer) {
        let dragElement = this.dragService.getDragElement();
        if (!dragElement || !dragElement.layer || this.timelineService.isExtending()) {
            return;
        }

        this.dragService.getDragElement().layer.clips =
            this.dragService.getDragElement().layer.clips.filter(
                (clip: Clip) => clip !== this.dragService.getDragElement().clip
            );
        this.dragService.getDragElement().layer = layer;
        layer.clips.push(this.dragService.getDragElement().clip);

        // Trigger Storyboard Object Change
        this.timelineService.storyboardChangeEvent.next(this.storyboard);
    }

    closeSRTEditor($event) {
      this.transcriptionEditing = false;
    }

    openSRTEditor($event) {
      this.transcriptionEditing = true;
    }
    
}



// WEBPACK FOOTER //
// ./src/app/storyboard/storyboard-edit/timeline/timeline.component.ts